import moment from 'moment';
import { useMemo } from 'react';
import { usePermissions } from 'react-admin';
import { CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { DashboardWrapper, RotatedAxisTick } from '../../components';
import { PERMISSIONS } from '../../constants';
import useDashboardContext from '../../hooks/useDashboardContext';
import { formatNumber, getDatesInRange } from '../../utils';

const chartMargin = { top: 10, right: 16, bottom: 10, left: 16 };
const yAxisDomain = [0, 'auto'];
const tooltipItemStyle = { color: '#333', fontSize: '14px' };
const tooltipContentStyle = {
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '5px',
    padding: '8px 16px',
};
const legendWrapperStyle = { top: -10, left: 20 };
const generatedDot = { r: 4 };
const generatedActiveDot = { r: 6 };
const redeemedDot = { r: 4 };
const redeemedActiveDot = { r: 6 };

const tooltipFormatter = (value) => `${formatNumber(value)} pontos`;
const yAxisTickFormatter = (value) => formatNumber(value);
const xAxisTickFormatter = (tick) => moment(tick, 'DD/MM/YYYY').format('DD/MM');

export const GeneratedXRedeemedGraph = () => {
    const { permissions } = usePermissions();
    const { from, to, pointsSummaryData, pointsSummaryLoading } = useDashboardContext();

    const chartData = useMemo(() => {
        const data = [];
        getDatesInRange(from, to).forEach(date =>
            data.push({ id: date, generatedpoints: 0, redeemedpoints: 0 })
        );

        if (pointsSummaryData?.perDay) {
            pointsSummaryData.perDay.forEach(point => {
                const currDate = data.find(chartDate => chartDate.id === point.date);
                if (currDate) {
                    currDate.generatedpoints = +point.generatedPoints || 0;
                    currDate.redeemedpoints = +point.redeemedPoints || 0;
                }
            });
        }

        return data;
    }, [pointsSummaryData, from, to]);

    const chartContent = useMemo(() => (
        <LineChart data={chartData} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
                dataKey="id"
                tick={<RotatedAxisTick fontSize={12} />}
                height={60}
                tickFormatter={xAxisTickFormatter}
            />
            <YAxis
                domain={yAxisDomain}
                type='number'
                tickFormatter={yAxisTickFormatter}
                width={50}
            />
            <Tooltip
                formatter={tooltipFormatter}
                itemStyle={tooltipItemStyle}
                contentStyle={tooltipContentStyle}
            />
            <Legend
                height={50}
                layout="horizontal"
                verticalAlign="top"
                wrapperStyle={legendWrapperStyle}
            />
            <Line
                type="monotone"
                dataKey="generatedpoints"
                stroke="#79CF62"
                strokeWidth={3}
                activeDot={generatedActiveDot}
                name="Pontos Gerados"
                dot={generatedDot}
                isAnimationActive={true}
            />
            <Line
                type="monotone"
                dataKey="redeemedpoints"
                stroke="#7869DF"
                strokeWidth={3}
                activeDot={redeemedActiveDot}
                name="Pontos Resgatados"
                dot={redeemedDot}
                isAnimationActive={true}
            />
        </LineChart>
    ), [chartData]);

    return (
        <DashboardWrapper
            permission={PERMISSIONS.GENERATED_REDEEMED_POINTS}
            width='45%'
            title="Pontos Gerados X Pontos Resgatados"
            count={chartData?.length || 0}
            loading={!pointsSummaryData && pointsSummaryLoading}
            content={chartContent}
        />
    );
};
